import React from 'react';
import { Box, Contact, ExpressSetupTile, ShimmerBox, Text } from '@common-fe/common-fe';

import { ADMIN_1_ID } from '@/modules/ExpressSetup/AddEditContactForm/hooks/useRoleFields.tsx';

import ExpressSetupActivityLogsBlock from './ActivityLogs/ExpressSetupActivityLogsBlock';
import AddEditContactModal from './AddEditContactModal';
import EditAddressModal from './EditAddressModal';
import ExpessSetupWrapper from './ExpessSetupWrapper';
import { ExpressSetupWizardStepType } from './ExpressSetup.types';
import ExpressSetupBanner from './ExpressSetupBanner';
import ExpressSetupFAQ from './ExpressSetupFAQ';
import ExpressSetupFooter from './ExpressSetupFooter';
import { useExpressSetupActions } from './hooks';

const ExpressSetup: React.FC = () => {
  const {
    updatedContact,
    contactModalVisible,
    setContactModalVisible,
    addressModalVisible,
    expressSetupData,
    isLoading,
    contacts,
    handleUpdateContact,
    handleRedirectToWizardStep,
    handleOpenAddressModal,
    handleOpenContactModal,
  } = useExpressSetupActions();

  return (
    <ExpessSetupWrapper>
      <ExpressSetupBanner
        expressSetupData={expressSetupData}
        isLoading={isLoading}
        onSetAddressModalVisible={handleOpenAddressModal}
        onClickMainContact={() => handleRedirectToWizardStep(ExpressSetupWizardStepType.MainContact)}
        onClickOpenContactModal={handleOpenContactModal}
        onClickUpdateContact={handleUpdateContact}
      />
      <Box margin={{ vertical: 'l' }}>
        {isLoading ? <ShimmerBox width="106px" height="32px" round="button2Round"  margin={{ bottom: 'l' }} /> : <Text weight={700} size="24px" color="textTitle" margin={{ bottom: 'l' }}>Required</Text>}
        <Box direction="row" gap="l" justify="between">
          {
            isLoading ? <ShimmerBox width="478px" height="200px" /> : (
              <ExpressSetupTile
                type="mainContact"
                mainContact={expressSetupData?.mainContact as Contact}
                onClick={() => handleRedirectToWizardStep(ExpressSetupWizardStepType.MainContact)}
                onEdit={() => handleRedirectToWizardStep(ExpressSetupWizardStepType.MainContact, true)}
                title="Main Contact"
                actionButtonText="Add Main Contact"
                description={expressSetupData?.mainContact?.role === ADMIN_1_ID ? 'Full Access' : expressSetupData?.mainContact ? 'No Access' : 'Responsible person for employer creation and activation.'}
                isLoading={isLoading}
                isActionDisabled={expressSetupData?.isAbandoned}
              />
            )
          }
          {
            isLoading ? <ShimmerBox width="478px" height="200px" /> : (
              <ExpressSetupTile
                type="bankAccount"
                accountNumber={expressSetupData?.accountNumberLast4}
                actionButtonText="Add Bank Account"
                title="Bank Account"
                onClick={() => handleRedirectToWizardStep(ExpressSetupWizardStepType.BankAccount)}
                isLoading={isLoading}
                isActionDisabled={expressSetupData?.isAbandoned}
              />
            ) 
          }
        </Box>
        {
          isLoading ? <ShimmerBox width="100%" height="420px" margin={{ top: 'l' }} /> : (
            <Box margin={{ top: 'l' }}>
              <ExpressSetupTile
                minHeight='420px'
                type="plans"
                plans={expressSetupData?.plans}
                planStartDate={expressSetupData?.planStartDate}
                planEndDate={expressSetupData?.planEndDate}
                onClick={() => handleRedirectToWizardStep(ExpressSetupWizardStepType.Plans)}
                title="Plans"
                actionButtonText="Configure"
                isLoading={isLoading}
                isActionDisabled={expressSetupData?.isAbandoned}
              />
            </Box>
          )
        }
      </Box>
      <Box margin={{ vertical: 'l' }}>
        {isLoading ? <ShimmerBox width="106px" height="32px" round="button2Round"  margin={{ bottom: 'l' }} /> : <Text weight={700} size="24px" color="textTitle" margin={{ bottom: 'l' }}>Optional</Text>}
        <Box direction="row" gap="l" justify="between">
          {
            isLoading ? <ShimmerBox width="478px" height="420px" /> : (
              <ExpressSetupTile
                width='478px'
                minHeight='420px'
                type="contacts"
                onClick={() => setContactModalVisible(true)}
                onUpdateContact={handleUpdateContact}
                contacts={contacts?.length ? contacts as Contact[] : undefined}
                title="Contacts"
                actionButtonText="Add Contact"
                isLoading={isLoading}
                isActionDisabled={expressSetupData?.isAbandoned}
              />
            )
          }
          {
            isLoading ? <ShimmerBox width="478px" height="200px" /> : (
              <ExpressSetupTile
                type="copays"
                title="Copays"
                minHeight='420px'
                isLoading={isLoading}
                copays={expressSetupData?.copays}
                isActionDisabled={expressSetupData?.isAbandoned}
              />
            ) 
          }
        </Box>
      </Box>
      <Box margin={{ vertical: 'l' }}>
        {isLoading ? <ShimmerBox width="106px" height="32px" round="button2Round"  margin={{ bottom: 'l' }} /> : <Text weight={700} size="24px" color="textTitle" margin={{ bottom: 'l' }}>FAQ</Text>}
        <ExpressSetupFAQ isLoading={isLoading} numberOfDaysBeforePlanStart={expressSetupData?.numberOfDaysBeforePlanStart} />
      </Box>
      {
        isLoading ? <ShimmerBox height="72px" margin={{ vertical: 'l' }} /> : <ExpressSetupActivityLogsBlock expressSetup={expressSetupData} />
      }
      <AddEditContactModal
        visible={contactModalVisible}
        onToggleVision={setContactModalVisible}
        onSetUpdatedContact={handleUpdateContact}
        updatedElement={updatedContact}
        isDisabled={expressSetupData?.isAbandoned}
      />
      <EditAddressModal
        visible={addressModalVisible}
        onToggleVision={handleOpenAddressModal}
        address={expressSetupData?.address}
      />
      <ExpressSetupFooter expressSetupData={expressSetupData} isLoading={isLoading} />
    </ExpessSetupWrapper>
  );
};
export default ExpressSetup;
